import React from "react";
import MuiAccordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { styled } from "@mui/material";
import "./StepAccordion.scss";

export enum AccordionMinimumHeight {
  EXTRASMALL = 250,
  SMALL = 400,
  MEDIUM = 600,
  LARGE = 800,
}

export enum AccordionBackgroundColor {
  GREY = "grey",
  GREEN = "green",
  PINK = "pink",
  BLUE = "blue",
  PURPLE = "purple",
  BEIGE = "beige",
  BROWN = "brown",
}

export enum ButtonColorEnum {
  WHITE = "white",
  PINK = "pink",
  PURPLE = "purple",
  GREEN = "green",
  GREY = "grey",
  DARK_GREEN = "dark_green",
}

export interface IStepAccordionProps {
  open: boolean;
  click(): void;
  label?: string;
  children?: React.ReactNode;
  minimumHeight?: AccordionMinimumHeight;
  loading?: boolean;
  loadingText?: string;
  loadingTitle?: string;
  firstButton?(): void;
  secondButton?(): void;
  firstButtonLabel?: string;
  secondButtonLabel?: string;
  firstButtonOutline?: boolean;
  secondButtonOutline?: boolean;
  firstButtonShadow?: boolean;
  secondButtonShadow?: boolean;
  firstButtonId?: string;
  secondButtonId?: string;
  secondButtonDisabled?: boolean;
  secondButtonType?: ButtonColorEnum;
  backgroundColor?: AccordionBackgroundColor;
}

const StepAccordion: React.FC<IStepAccordionProps> = ({
  open,
  click,
  label = "Accordion header",
  children,
  minimumHeight = AccordionMinimumHeight.SMALL,
  loading = false,
  loadingText,
  loadingTitle,
  firstButton,
  secondButton,
  firstButtonLabel = "tilbage",
  secondButtonLabel = "næste",
  firstButtonOutline = false,
  secondButtonOutline = false,
  firstButtonShadow = false,
  secondButtonShadow = false,
  firstButtonId,
  secondButtonId,
  secondButtonDisabled = false,
  secondButtonType = ButtonColorEnum.DARK_GREEN,
  backgroundColor,
}) => {
  const getMinimumHeightClass = (): string => {
    if (minimumHeight === AccordionMinimumHeight.EXTRASMALL)
      return "step-accordion-open-extrasmall";
    if (minimumHeight === AccordionMinimumHeight.SMALL)
      return "step-accordion-open-small";
    if (minimumHeight === AccordionMinimumHeight.MEDIUM)
      return "step-accordion-open-medium";
    return "step-accordion-open-large";
  };

  const getBackgroundClass = (): string => {
    if (!backgroundColor) return "";
    return `background-${backgroundColor}`;
  };

  const getSecondButtonClass = (): string => {
    const classes = ["step-accordion-button", "step-accordion-button-second"];
    if (secondButtonType === ButtonColorEnum.DARK_GREEN)
      classes.push("button-dark-green");
    if (secondButtonType === ButtonColorEnum.GREY) classes.push("button-grey");
    if (secondButtonOutline) classes.push("button-outline");
    if (secondButtonShadow) classes.push("button-shadow");
    return classes.join(" ");
  };

  const getFirstButtonClass = (): string => {
    const classes = ["step-accordion-button", "step-accordion-button-first"];
    if (firstButtonOutline) classes.push("button-outline");
    if (firstButtonShadow) classes.push("button-shadow");
    return classes.join(" ");
  };

  return (
    <MuiAccordion
      expanded={open}
      onChange={() => click()}
      className={`step-accordion ${open ? getMinimumHeightClass() : "step-accordion-closed"} ${getBackgroundClass()}`}
      TransitionProps={{ timeout: 300 }}
      disableGutters
      elevation={0}
    >
      <StyledAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        className="step-accordion-header"
      >
        <span className="step-accordion-label">{label}</span>
      </StyledAccordionSummary>

      <AccordionDetails className="step-accordion-details">
        {loading && (
          <div className="overlay-container">
            <div className="overlay-title">{loadingTitle}</div>

            {loadingTitle && <div className="overlay-line" />}
            
            <div className="overlay-message">{loadingText}</div>
            
            <div className="overlay-spinner">
              <CircularProgress size={48} sx={{ color: "#25D287" }} />
            </div>
          </div>
        )}
        
        <div className="step-accordion-content">{children}</div>
        
        <div className="step-accordion-footer">
          {firstButton && (
            <Button
              id={firstButtonId}
              className={getFirstButtonClass()}
              onClick={(e) => {
                e.stopPropagation();
                firstButton();
              }}
              variant="outlined"
            >
              {firstButtonLabel}
            </Button>
          )}

          <span className="footer-span" />
          
          {secondButton && (
            <Button
              id={secondButtonId}
              className={getSecondButtonClass()}
              onClick={(e) => {
                e.stopPropagation();
                secondButton();
              }}
              disabled={secondButtonDisabled}
              variant="contained"
            >
              {secondButtonLabel}
            </Button>
          )}
        </div>
      </AccordionDetails>
    </MuiAccordion>
  );
};

export { StepAccordion };

const StyledAccordionSummary = styled(AccordionSummary)`
  min-height: 95px !important;
  padding: 0 32px 0 0;

  .MuiAccordionSummary-content {
    margin: 0;
  }

  .MuiAccordionSummary-expandIconWrapper {
    .MuiSvgIcon-root {
      width: 36px;
      height: 36px;
      color: #323232;
    }
  }
`;
